/**
 * SAP CPI Monitoring Dashboard
 * Copyright (c) 2026 David Jahn
 * Contact: david.jahnxx@gmail.com
 */
import { useState, useEffect, useCallback, useRef, useMemo, Component } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import "./i18n";
import { ThemeProvider, CssBaseline } from "@mui/material";
import MuiButton from "@mui/material/Button";
import MuiTextField from "@mui/material/TextField";
import MuiAvatar from "@mui/material/Avatar";
import MuiIconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";

// === Design System (Phase 0) ===
import { FONT, DARK } from "./theme/tokens";
import { buildMuiTheme } from "./theme/muiTheme";
import { buildCSS } from "./theme/cssVariables";
import { ThemeCtx } from "./theme/ThemeContext";
import { api, v, getToken, setToken, hatCicdZugang } from "./utils/helpers";
import useDashboardStore from "./store/useDashboardStore";

// Backward-compatible alias used in this file
const setTk = setToken;


// === Page Components (Phase 1 - extracted from monolith) ===
import LoginPage from "./pages/LoginPage";
import SpacesHome from "./pages/SpacesHome";
import SpaceDetail from "./pages/SpaceDetail";
import Dashboard from "./pages/Dashboard";
import InviteAccept from "./pages/InviteAccept";
import SettingsModal from "./pages/SettingsModal";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import AiChat from "./pages/AiChat";
import AlertingPage from "./pages/AlertingPage";
import AuditLog from "./pages/AuditLog";
import TransportApprovals from "./pages/TransportApprovals";
import PastebinPage from "./pages/PastebinPage";
import PartnerDirectory from "./pages/PartnerDirectory";
import PipelineTopology from "./pages/PipelineTopology";
import PixelArt from "./pages/PixelArt";
import DokuPage from "./pages/DokuPage";
import TransportV2Page from "./pages/transport-v2/TransportV2Page";
import BenutzerAnlegen from "./pages/BenutzerAnlegen";
import BenutzerVerwalten from "./pages/BenutzerVerwalten";
import CicdSetup from "./pages/CicdSetup";
import CicdPipeline from "./pages/CicdPipeline";
import AiIflowBuilder from "./pages/AiIflowBuilder";
import AiInsights from "./pages/AiInsights";
import ChatBubble from "./components/ChatBubble";
import ChatPanel from "./components/ChatPanel";
import { useSSE } from "./hooks/useSSE";

// === TanStack Query - globale Instanz (immer aktiv, aber Queries nur wenn experimentalCaching=true) ===
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// SSE-Connector: Muss innerhalb von QueryClientProvider gerendert werden
function SSEConnector() {
  useSSE();
  return null;
}

// === Error Boundary (catches runtime crashes → shows error instead of black screen) ===
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("React Error Boundary caught:", error, info); }
  render() {
    if (this.state.hasError) {
      return <div style={{ minHeight: "100vh", background: "#050810", color: "#edf0f7", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT }}>
        <div style={{ textAlign: "center", maxWidth: 500, padding: 40 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <h2 style={{ marginBottom: 8 }}>{i18n.t("navigation:fehler.titel")}</h2>
          <p style={{ color: "#8b9dc3", marginBottom: 16 }}>{this.state.error?.message || i18n.t("navigation:fehler.unbekannt")}</p>
          <pre style={{ textAlign: "left", background: "#0e1422", padding: 16, borderRadius: 8, fontSize: 11, overflow: "auto", maxHeight: 200, color: "#ef4444" }}>{this.state.error?.stack?.split("\n").slice(0, 8).join("\n")}</pre>
          <button onClick={() => { this.setState({ hasError: false }); window.location.href = "/"; }} style={{ marginTop: 16, padding: "10px 24px", borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", fontSize: 14, cursor: "pointer" }}>{i18n.t("navigation:fehler.zurStartseite")}</button>
        </div>
      </div>;
    }
    return this.props.children;
  }
}

export default function App(){
  const{t}=useTranslation(["navigation","gemeinsam"]);
  const[user,setUser]=useState(null);const[checking,setChecking]=useState(true);
  const[page,setPage]=useState("spaces");const[spaces,setSpaces]=useState([]);const[activeSpace,setActiveSpace]=useState(null);const[activeInst,setActiveInst]=useState(null);
  const[pdPreselectedInst,setPdPreselectedInst]=useState(null);const[pdPreselectedPid,setPdPreselectedPid]=useState(null);
  const[chatPrefill,setChatPrefill]=useState(null);
  const[targetFlowName,setTargetFlowName]=useState(null);
  const[targetTab,setTargetTab]=useState(null);
  // dashboardSubTab haelt den aktiven Subtab fuer Sidebar-Highlight stabil.
  // targetTab wird von Dashboard.jsx nach Konsum geleert - dashboardSubTab nicht.
  const[dashboardSubTab,setDashboardSubTab]=useState("monitor");
  const[showMenu,setShowMenu]=useState(false);const[showSettings,setShowSettings]=useState(false);const[mobileNavOpen,setMobileNavOpen]=useState(false);
  const[branding,setBranding]=useState({title:"CPI Monitor",subtitle:"SAP Integration Suite Dashboard",logoUrl:"",faviconUrl:""});
  // CI/CD-Bindung je Space (zentral geladen, einmal pro Session/Refresh, an SpacesHome+SpaceDetail durchgereicht).
  // spaces enthaelt NUR gebundene Spaces. tokenSet sagt, ob GitHub verbunden ist -
  // configured und pipelineReady=false zeigt einen Halbzustand ("eingerichtet, GitHub fehlt").
  const[cicdStatus,setCicdStatus]=useState({tokenSet:false,spaces:{}});
  const[chatOpen,setChatOpen]=useState(false);
  const hasAiInstances=true;
  const[spacesHover,setSpacesHover]=useState(false);const[hoverSpaceId,setHoverSpaceId]=useState(null);const[hoverInstances,setHoverInstances]=useState({});
  // Sidebar-Tree: aufklappbare Spaces > Instanzen > Monitor/Content
  const[treeExpSpaces,setTreeExpSpaces]=useState({});
  const[treeExpInsts,setTreeExpInsts]=useState({});
  const inviteToken=window.location.pathname.startsWith("/invite/")?window.location.pathname.split("/invite/")[1]:null;

  // === URL Routing (#6) ===
  const pageRef=useRef(page);pageRef.current=page;
  const buildUrl=(pg,space,inst)=>{
    if(pg==="spaces")return"/";
    if(pg==="space"&&space)return`/space/${space.id}`;
    if(pg==="dashboard"&&inst)return`/space/${space?.id||activeSpace?.id||inst.spaceId||"_"}/${inst.id}`;
    if(pg==="chat")return"/chat";
    if(pg==="ai-builder")return"/ai-builder";
    if(pg==="alerting")return"/alerting";
    if(pg==="audit")return"/audit";
    if(pg==="approvals")return"/approvals";
    if(pg==="pastebin")return"/pastebin";
    if(pg==="pd")return"/pd";
    if(pg==="pipeline")return"/pipeline";
    if(pg==="pixel-art")return"/pixel-art";
    if(pg==="doku")return"/doku";
    if(pg==="transport")return"/transport";
    if(pg==="transport-v2")return"/transport-v2";
    if(pg==="benutzer-anlegen")return"/benutzer-anlegen";
    if(pg==="benutzer-verwalten")return"/benutzer-verwalten";
    if(pg==="cicd-setup")return"/cicd-setup";
    if(pg==="cicd-pipeline")return"/cicd-pipeline";
    if(pg==="ai-insights")return"/ai-insights";
    if(pg==="invite"&&inviteToken)return`/invite/${inviteToken}`;
    return"/";
  };
  const navigate=(pg,space,inst,opts)=>{
    if(opts?.flowName)console.log("[Nav] →",pg,"flowName:",opts.flowName,"inst:",inst?.id);
    setPage(pg);if(space!==undefined)setActiveSpace(space);if(inst!==undefined)setActiveInst(inst);
    if(opts?.flowName)setTargetFlowName(opts.flowName);
    if(opts?.tab)setTargetTab({tab:opts.tab,nonce:Date.now()});
    // dashboardSubTab stabil halten fuer Sidebar-Highlight (unabhaengig von targetTab-Clear)
    if(pg==="dashboard"){if(opts?.tab==="content"||opts?.flowName)setDashboardSubTab("content");else if(opts?.tab==="monitor")setDashboardSubTab("monitor");}
    else if(pg!=="dashboard")setDashboardSubTab("monitor");
    let url=buildUrl(pg,space||activeSpace,inst||activeInst);
    if(opts?.flowName&&pg==="dashboard")url+="/content/"+encodeURIComponent(opts.flowName);
    else if(opts?.tab==="content"&&pg==="dashboard")url+="/content";
    window.history.pushState({page:pg,tab:opts?.tab},document.title,url);
    setMobileNavOpen(false);
  };

  // Parse URL on load
  useEffect(()=>{
    const path=window.location.pathname;
    const parts=path.split("/").filter(Boolean);
    if(parts[0]==="chat"){setPage("chat");}
    else if(parts[0]==="alerting"){setPage("alerting");}
    else if(parts[0]==="audit"){setPage("audit");}
    else if(parts[0]==="approvals"){setPage("approvals");}
    else if(parts[0]==="pastebin"){setPage("pastebin");}
    else if(parts[0]==="pd"){setPage("pd");}
    else if(parts[0]==="pipeline"){setPage("pipeline");}
    else if(parts[0]==="pixel-art"){setPage("pixel-art");}
    else if(parts[0]==="doku"){setPage("doku");}
    else if(parts[0]==="transport"){setPage("transport");}
    else if(parts[0]==="transport-v2"){setPage("transport-v2");}
    else if(parts[0]==="benutzer-anlegen"){setPage("benutzer-anlegen");}
    else if(parts[0]==="benutzer-verwalten"){setPage("benutzer-verwalten");}
    else if(parts[0]==="cicd-setup"){setPage("cicd-setup");}
    else if(parts[0]==="cicd-pipeline"){setPage("cicd-pipeline");}
    // Altes Lesezeichen /transport-freigabe wird auf die neue Freigabe-Seite gemappt.
    else if(parts[0]==="transport-freigabe"){setPage("approvals");}
    else if(parts[0]==="ai-insights"){setPage("ai-insights");}
    else if(parts[0]==="ai-builder"){setPage("ai-builder");}
    else if(parts[0]==="invite"&&parts[1]){setPage("invite");}
    else if(parts[0]==="space"&&parts[1]){
      setPage(parts[2]?"dashboard":"space");
      const params=new URLSearchParams(window.location.search);
      const flowParam=params.get("flow");
      const pathFlow=(parts[3]==="content"&&parts[4])?decodeURIComponent(parts[4]):null;
      if(flowParam||pathFlow)setTargetFlowName(flowParam||pathFlow);
      // Fix B: targetTab beim Direktaufruf der content-URL setzen, damit Sidebar-Highlight stimmt
      if(parts[2]&&parts[3]==="content"){setTargetTab({tab:"content",nonce:Date.now()});setDashboardSubTab("content");}
      else if(parts[2])setDashboardSubTab("monitor");
      window.__pendingRoute={spaceId:parts[1],instId:parts[2]||null};
    }
  },[]);

  // Listen for navigate-to-partner-directory events from child components
  useEffect(()=>{
    const onNavPd=(e)=>{setPdPreselectedInst(e.detail?.instId||null);setPdPreselectedPid(e.detail?.pid||null);setPage("pd");window.history.pushState({page:"pd"},"","/pd");setMobileNavOpen(false);};
    window.addEventListener("cpi-nav-pd",onNavPd);return()=>window.removeEventListener("cpi-nav-pd",onNavPd);
  },[]);

  useEffect(()=>{
    const onChatPrefill=(e)=>{setChatPrefill(e.detail?.message||null);setPage("chat");window.history.pushState({page:"chat"},"","/chat");setMobileNavOpen(false);};
    window.addEventListener("cpi-chat-prefill",onChatPrefill);return()=>window.removeEventListener("cpi-chat-prefill",onChatPrefill);
  },[]);

  useEffect(()=>{fetch("/api/branding").then(r=>r.json()).then(setBranding).catch(()=>{});},[]);
  useEffect(()=>{
    let l=document.querySelector("link[rel~='icon']");
    if(branding.faviconUrl){
      if(!l){l=document.createElement("link");l.rel="icon";document.head.appendChild(l);}
      l.href=branding.faviconUrl;
    }else if(l){
      // Leeres Icon setzen statt Element entfernen: Browser behalten das alte Tab-Icon sonst aus dem Cache
      l.href="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E";
    }
    if(branding.title)document.title=branding.title;
  },[branding]);
  useEffect(()=>{const t=getToken();if(!t){setChecking(false);return;}api("/api/auth/me").then(setUser).catch(()=>setTk(null)).finally(()=>setChecking(false));},[]);

  // Zustand Store: experimentalCaching Flag aus Settings laden
  useEffect(()=>{
    if(!user)return;
    api("/api/auth/settings").then(s=>{
      useDashboardStore.getState().setExperimentalCaching(s.experimentalCaching||false);
    }).catch(()=>{});
  },[user]);

  // CI/CD-Bindung je Space einmal zentral laden (guenstig, keine GitHub-Calls).
  // Wird von SpacesHome, SpaceDetail und der Sidebar geteilt genutzt.
  const loadCicdStatus=useCallback(()=>{
    if(!user)return;
    api("/api/cicd/spaces-status")
      .then(r=>setCicdStatus({tokenSet:!!r?.tokenSet,spaces:r?.spaces||{}}))
      .catch(()=>setCicdStatus({tokenSet:false,spaces:{}}));
  },[user]);
  useEffect(()=>{loadCicdStatus();},[loadCicdStatus]);

  const loadSpaces=useCallback(async()=>{
    let sp;
    try{sp=await api("/api/spaces");setSpaces(sp);}
    catch(e){console.error("[Route] /api/spaces failed:",e);return;}
    if(!window.__pendingRoute)return;
    const{spaceId,instId}=window.__pendingRoute;delete window.__pendingRoute;
    if(!spaceId){return;}
    let space=sp.find(s=>s.id===spaceId);
    if(!space&&instId){
      for(const s of sp){
        try{
          const insts=await api(`/api/spaces/${s.id}/instances`);
          const found=insts.find(i=>i.id===instId);
          if(found){space=s;setActiveSpace(s);setActiveInst(found);setPage("dashboard");return;}
        }catch{}
      }
    }
    if(!space){console.warn("[Route] Space",spaceId,"not found");setPage("spaces");return;}
    setActiveSpace(space);
    if(!instId)return;
    try{
      const insts=await api(`/api/spaces/${space.id}/instances`);
      const inst=insts.find(i=>i.id===instId);
      if(inst){setActiveInst(inst);setPage("dashboard");}
      else{console.warn("[Route] Instance",instId,"not found in space",space.id);setPage("space");}
    }catch(e){console.error("[Route] instances failed:",e);setPage("space");}
  },[]);
  useEffect(()=>{if(user&&!user.mustChangePassword){loadSpaces();if(inviteToken)setPage("invite");}},[user,loadSpaces,inviteToken]);

  useEffect(()=>{
    const onPop=()=>{
      const path=window.location.pathname;const parts=path.split("/").filter(Boolean);
      // Top-Level-Pages: targetTab/targetFlowName resetten, damit beim Zurueckklick auf eine Instanz nicht der alte Subtab haengen bleibt
      const topLevel=["chat","ai-builder","alerting","audit","approvals","pastebin","pd","pipeline","pixel-art","doku","transport","transport-v2","benutzer-anlegen","benutzer-verwalten","cicd-setup","cicd-pipeline","ai-insights"];
      // Die alte Transport-App ist entfernt. Lesezeichen auf /transport zeigen
      // weiter, statt ins Leere zu laufen.
      if(parts[0]==="transport"){setPage("transport-v2");setTargetTab(null);setTargetFlowName(null);setDashboardSubTab("monitor");return;}
      // Altes Lesezeichen /transport-freigabe -> neue Freigabe-Seite (Route "approvals").
      if(parts[0]==="transport-freigabe"){setPage("approvals");setTargetTab(null);setTargetFlowName(null);setDashboardSubTab("monitor");return;}
      if(topLevel.includes(parts[0])){setPage(parts[0]);setTargetTab(null);setTargetFlowName(null);setDashboardSubTab("monitor");return;}
      if(parts[0]==="space"&&parts[2]){
        const flow=(parts[3]==="content"&&parts[4])?decodeURIComponent(parts[4]):null;
        setTargetFlowName(flow||null);
        // Fix A: Subtab aus URL ableiten, sonst zeigt Dashboard nach Back den falschen Tab
        if(parts[3]==="content"){setTargetTab({tab:"content",nonce:Date.now()});setDashboardSubTab("content");}
        else{setTargetTab({tab:"monitor",nonce:Date.now()});setDashboardSubTab("monitor");}
        const existingSpace=spaces.find(s=>s.id===parts[1]);
        if(existingSpace&&activeInst?.id===parts[2]){setActiveSpace(existingSpace);setPage("dashboard");}
        else{window.__pendingRoute={spaceId:parts[1],instId:parts[2]};setPage("dashboard");loadSpaces();}
      }
      else if(parts[0]==="space"&&parts[1]){
        const existingSpace=spaces.find(s=>s.id===parts[1]);
        if(existingSpace){setActiveSpace(existingSpace);setActiveInst(null);setPage("space");setTargetTab(null);setTargetFlowName(null);}
        else{setPage("spaces");setActiveSpace(null);setActiveInst(null);setTargetTab(null);setTargetFlowName(null);}
      }
      else{setPage("spaces");setActiveSpace(null);setActiveInst(null);setTargetTab(null);setTargetFlowName(null);}
    };
    window.addEventListener("popstate",onPop);return()=>window.removeEventListener("popstate",onPop);
  },[spaces,activeInst,loadSpaces]);
  const logout=()=>{setTk(null);setUser(null);setSpaces([]);setPage("spaces");setActiveSpace(null);setActiveInst(null);setTargetTab(null);setTargetFlowName(null);setDashboardSubTab("monitor");setPdPreselectedInst(null);setPdPreselectedPid(null);setChatPrefill(null);setShowMenu(false);setMobileNavOpen(false);window.history.pushState({},"","/");};

  // Hover instances loader for nav dropdown
  const loadHoverInstances=async(spaceId)=>{
    if(hoverInstances[spaceId])return;
    try{const insts=await api(`/api/spaces/${spaceId}/instances`);setHoverInstances(p=>({...p,[spaceId]:insts}));}catch{}
  };

  const[theme,setTheme]=useState(()=>localStorage.getItem("cpi_theme")||"dark");
  // Theme-Wechsel mit weichem Crossfade: View-Transitions-API wenn verfuegbar,
  // sonst kurzzeitige Farb-Transitions auf allen Elementen (CSS .theme-fade).
  const toggleTheme=()=>{
    const t=theme==="dark"?"light":"dark";
    const apply=()=>{setTheme(t);localStorage.setItem("cpi_theme",t);};
    if(document.startViewTransition){document.startViewTransition(apply);}
    else{
      document.documentElement.classList.add("theme-fade");
      apply();
      setTimeout(()=>document.documentElement.classList.remove("theme-fade"),380);
    }
  };
  // Sync data-theme to <html> so MUI Portal dialogs inherit CSS variables
  useEffect(()=>{document.documentElement.setAttribute("data-theme",theme);},[theme]);
  // Hintergrund-Parallax: Papier-Textur-Ebene driftet beim Scrollen sanft mit (GPU-transform).
  useEffect(()=>{
    if(typeof window==="undefined")return;
    if(window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches)return;
    const SIZE=22; let ticking=false;
    const raf=window.requestAnimationFrame?window.requestAnimationFrame.bind(window):(f)=>setTimeout(f,16);
    const onScroll=()=>{ if(ticking)return; ticking=true; raf(()=>{
      const el=document.querySelector(".iio-bgfx");
      if(el){ const sy=window.pageYOffset||document.documentElement.scrollTop||0; el.style.transform="translate3d(0,"+(-((sy*0.25)%SIZE))+"px,0)"; }
      ticking=false;
    }); };
    window.addEventListener("scroll",onScroll,{passive:true});
    return ()=>window.removeEventListener("scroll",onScroll);
  },[]);
  const muiTheme=useMemo(()=>buildMuiTheme(theme),[theme]);
  const[sidebarOpen,setSidebarOpen]=useState(true);
  // - Sidebar v2: Rail-Modus, Spaces-Gruppe, User-Menü, gleitender Marker -
  const[sbRail,setSbRail]=useState(()=>{try{return localStorage.getItem("iio_sb_rail")==="1";}catch{return false;}});
  const toggleSbRail=()=>{setSbRail(r=>{const n=!r;try{localStorage.setItem("iio_sb_rail",n?"1":"0");}catch{}return n;});};
  const[sbGrpOpen,setSbGrpOpen]=useState(true);
  const[sbUserMenu,setSbUserMenu]=useState(false);
  const sbScrollRef=useRef(null);
  const sbMarkerRef=useRef(null);
  const[sbFades,setSbFades]=useState({top:false,bot:false});
  const updateSbFades=useCallback(()=>{
    const sc=sbScrollRef.current;if(!sc)return;
    setSbFades(p=>{
      const top=sc.scrollTop>4, bot=sc.scrollTop<sc.scrollHeight-sc.clientHeight-4;
      return (p.top===top&&p.bot===bot)?p:{top,bot};
    });
  },[]);
  // Marker via Bounding-Rects relativ zum Scroll-Inhalt (offsetTop wäre durch
  // verschachtelte position:relative-Eltern versetzt). Leaf vor Item bevorzugen.
  const repositionSbMarker=useCallback(()=>{
    const sc=sbScrollRef.current,mk=sbMarkerRef.current;
    if(!sc||!mk)return;
    const el=sc.querySelector(".iio-sb2-leaf.is-active")||sc.querySelector(".iio-sb2-item.is-active");
    if(!el||el.getBoundingClientRect().height<4){mk.style.opacity="0";return;}
    const sr=sc.getBoundingClientRect(),er=el.getBoundingClientRect();
    const h=Math.max(16,Math.min(34,Math.round(er.height)-8));
    mk.style.height=h+"px";
    mk.style.transform="translateY("+((er.top-sr.top)+sc.scrollTop+(er.height-h)/2)+"px)";
    mk.style.opacity="1";
  },[]);
  // Nur bei Layout-relevanten Aenderungen neu positionieren (nicht bei jedem Render)
  useEffect(()=>{
    const raf=requestAnimationFrame(()=>{repositionSbMarker();updateSbFades();});
    const t=setTimeout(()=>{repositionSbMarker();updateSbFades();},360);
    return()=>{cancelAnimationFrame(raf);clearTimeout(t);};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[page,dashboardSubTab,activeInst,sbGrpOpen,treeExpSpaces,treeExpInsts,sbRail,spaces,hoverInstances]);
  useEffect(()=>{
    if(!sbUserMenu)return;
    const h=()=>setSbUserMenu(false);
    document.addEventListener("click",h);
    return()=>document.removeEventListener("click",h);
  },[sbUserMenu]);
  const[spacesExpanded,setSpacesExpanded]=useState(true);
  const[searchQuery,setSearchQuery]=useState("");const[searchResults,setSearchResults]=useState(null);const[searchLoading,setSearchLoading]=useState(false);
  const searchTimeout=useRef(null);

  // Global search
  const runSearch=async(q)=>{
    if(!q.trim()){setSearchResults(null);return;}
    setSearchLoading(true);
    try{
      const results={iflows:[],packages:[],instances:[]};
      for(const sp of spaces){
        try{const insts=await api(`/api/spaces/${sp.id}/instances`);
          for(const inst of insts){
            if(inst.name.toLowerCase().includes(q.toLowerCase())) results.instances.push({...inst,spaceName:sp.name,spaceId:sp.id});
            try{const flows=await api(`/api/${inst.id}/iflows`);
              flows.filter(f=>f.Name?.toLowerCase().includes(q.toLowerCase())).forEach(f=>results.iflows.push({...f,instanceName:inst.name,instanceId:inst.id,spaceId:sp.id,spaceName:sp.name}));
            }catch{}
          }
        }catch{}
      }
      setSearchResults(results);
    }catch{}setSearchLoading(false);
  };
  const handleSearch=(val)=>{setSearchQuery(val);clearTimeout(searchTimeout.current);
    searchTimeout.current=setTimeout(()=>runSearch(val),500);
  };

  const CSS = buildCSS();

  if(checking)return<QueryClientProvider client={queryClient}><ErrorBoundary><ThemeProvider theme={muiTheme}><CssBaseline/><div data-theme={theme}><style>{CSS}</style><div style={{minHeight:"100vh",background:v("bg"),display:"flex",alignItems:"center",justifyContent:"center",color:v("text-3")}}>{t("gemeinsam:zustand.laedt")}</div></div></ThemeProvider></ErrorBoundary></QueryClientProvider>;
  if(!user&&page==="pixel-art")return<QueryClientProvider client={queryClient}><ErrorBoundary><ThemeProvider theme={muiTheme}><CssBaseline/><ThemeCtx.Provider value={{theme,toggle:toggleTheme}}><div data-theme={theme}><style>{CSS}</style><div style={{minHeight:"100vh",background:v("bg")}}><PixelArt isGuest/></div></div></ThemeCtx.Provider></ThemeProvider></ErrorBoundary></QueryClientProvider>;
  if(!user)return<QueryClientProvider client={queryClient}><ErrorBoundary><ThemeProvider theme={muiTheme}><CssBaseline/><ThemeCtx.Provider value={{theme,toggle:toggleTheme}}><div data-theme={theme}><style>{CSS}</style><LoginPage onLogin={setUser} inviteToken={inviteToken}/></div></ThemeCtx.Provider></ThemeProvider></ErrorBoundary></QueryClientProvider>;
  if(user.mustChangePassword)return<QueryClientProvider client={queryClient}><ErrorBoundary><ThemeProvider theme={muiTheme}><CssBaseline/><ThemeCtx.Provider value={{theme,toggle:toggleTheme}}><div data-theme={theme}><style>{CSS}</style><ForcePasswordChange onDone={()=>setUser({...user,mustChangePassword:false})}/></div></ThemeCtx.Provider></ThemeProvider></ErrorBoundary></QueryClientProvider>;

  return(
    <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
    <SSEConnector/>
    <ThemeProvider theme={muiTheme}><CssBaseline/>
    <ThemeCtx.Provider value={{theme,toggle:toggleTheme}}>
    <div data-theme={theme} className="iio">
      <style>{CSS}</style>
      {/* Hintergrund-FX: Papier-Textur-Ebene (fixiert, hinter dem Inhalt, Scroll-Parallax) */}
      <div className="iio-bgfx" aria-hidden="true"/>

      {/* === MOBILE OVERLAY BACKDROP === */}
      <div className={"iio-mobile-backdrop"+(mobileNavOpen?" is-open":"")} onClick={()=>setMobileNavOpen(false)}/>

      <div className={"iio-app"+(sbRail?" is-rail":"")}>

        {/* === SIDEBAR (v2: Navigation scrollbar | User fix)
             Die Marke sitzt in der Kopfleiste, siehe .iio-topbar-lead. === */}
        <aside className={"iio-sidebar"+(mobileNavOpen?" is-open":"")}>

          {/* Zone 2: scrollbare Navigation mit gleitendem Marker.
              Die Verlaufskanten liegen als Auflage AUF dem Mantel, nicht im
              Scroller - nur so enden sie exakt auf der Trennlinie zum Benutzer. */}
          <div className="iio-sb2-navwrap">
          <nav className="iio-sb2-scroll" ref={sbScrollRef} onScroll={updateSbFades}>
            <span className="iio-sb2-marker" ref={sbMarkerRef} style={{transform:"translateY(0)",opacity:0}}/>

            <div className="iio-sb2-sec">
              <div className="iio-sb2-label">{t("gruppe.arbeitsbereich")}</div>

              {/* Spaces: Gruppe auf-/zuklappbar, Kopf navigiert zur Übersicht */}
              <div className={"iio-sb2-grp"+(sbGrpOpen?" open":"")}>
                <button type="button"
                  className={"iio-sb2-item"+((page==="spaces"||page==="space"||page==="dashboard")?" is-active":"")}
                  title={t("eintrag.spaces")}
                  onClick={()=>{setSbGrpOpen(true);setTargetTab(null);setTargetFlowName(null);setDashboardSubTab("monitor");navigate("spaces",null,null);loadSpaces();setMobileNavOpen(false);}}>
                  <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"><path d="M4 8.5 12 4l8 4.5-8 4.5-8-4.5z"/><path d="M4 13l8 4.5 8-4.5" opacity=".55"/><path d="M4 17.5 12 22l8-4.5" opacity=".3"/></svg></span>
                  <span className="lbl">{t("eintrag.spaces")}</span>
                  {spaces.length>0&&<span className="cnt">{spaces.length}</span>}
                  <span className="iio-sb2-grpchev" role="button" tabIndex={0} aria-label={sbGrpOpen?t("baum.spacesZuklappen"):t("baum.spacesAufklappen")}
                    onClick={(e)=>{e.stopPropagation();setSbGrpOpen(o=>!o);}}
                    onKeyDown={(e)=>{if(e.key==="Enter"||e.key===" "){e.preventDefault();e.stopPropagation();setSbGrpOpen(o=>!o);}}}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                  </span>
                </button>
                <div className="iio-sb2-grpbody"><div>
                  <div className="iio-sb2-tree">
                    {spaces.map((sp,spIdx)=>{
                      const isSpaceOpen=!!treeExpSpaces[sp.id];
                      const insts=hoverInstances[sp.id]||[];
                      // CI/CD-Bindung fuer diesen Space (kein Eintrag = nicht gebunden)
                      const cicd=cicdStatus?.spaces?.[sp.id];
                      const cicdReady=!!cicd?.pipelineReady;
                      const cicdConfigured=!!cicd?.configured;
                      const cicdTitle=cicdReady
                        ? t("cicd.bereit")
                        : cicdConfigured
                          ? t("cicd.ohneGithub")
                          : null;
                      const glyphs=[
                        <svg key="g0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="4" y="4" width="16" height="16" rx="3"/><path d="M4 12h16M12 4v16" opacity=".5"/></svg>,
                        <svg key="g1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3l8 5v8l-8 5-8-5V8l8-5z"/><path d="M12 21V11M4 8l8 3 8-3" opacity=".5"/></svg>,
                        <svg key="g2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="8"/><path d="M12 8v8M8 12h8" opacity=".55"/></svg>,
                        <svg key="g3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 5h14v14H5z" transform="rotate(45 12 12)"/><path d="M12 8.5v7" opacity=".5"/></svg>,
                      ];
                      return (
                        <div key={sp.id} className={"iio-sb2-sp"+(isSpaceOpen?" open":"")}>
                          {/* Zeile navigiert zur Space-Übersicht, Chevron (separates Klickziel) klappt die Instanzen auf/zu */}
                          <button type="button" className="iio-sb2-sprow" title={t("baum.spaceOeffnen",{name:sp.name})}
                            onClick={()=>{setTargetTab(null);setTargetFlowName(null);navigate("space",sp,null);}}>
                            <span className="iio-sb2-spchev" role="button" tabIndex={0} aria-label={isSpaceOpen?t("baum.instanzenZuklappen"):t("baum.instanzenAufklappen")}
                              onClick={(e)=>{
                                e.stopPropagation();
                                const next=!isSpaceOpen;
                                setTreeExpSpaces(p=>({...p,[sp.id]:next}));
                                if(next)loadHoverInstances(sp.id);
                              }}
                              onKeyDown={(e)=>{
                                if(e.key==="Enter"||e.key===" "){
                                  e.preventDefault();e.stopPropagation();
                                  const next=!isSpaceOpen;
                                  setTreeExpSpaces(p=>({...p,[sp.id]:next}));
                                  if(next)loadHoverInstances(sp.id);
                                }
                              }}>
                              <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                            </span>
                            <span className="glyph">{glyphs[spIdx%glyphs.length]}</span>
                            <span className="lbl">{sp.name}</span>
                            {/* Text statt Piktogramm: das Wellen-Icon sagte nichts, das
                                Wort benennt den Sachverhalt. Der title unterscheidet
                                weiter zwischen bereit und "GitHub fehlt". */}
                            {cicdTitle&&(
                              <span className={"cicd-mark"+(cicdReady?"":" is-off")} title={cicdTitle} aria-label={cicdTitle}>
                                CI/CD
                              </span>
                            )}
                            {insts.length>0&&<span className="cnt">{insts.length}</span>}
                          </button>
                          <div className="iio-sb2-spbody"><div className="iio-sb2-instwrap">
                            {insts.map(inst=>{
                              const isInstOpen=!!treeExpInsts[inst.id];
                              const isInstActive=page==="dashboard"&&activeInst?.id===inst.id;
                              const isMonActive=isInstActive&&dashboardSubTab!=="content";
                              const isContentActive=isInstActive&&dashboardSubTab==="content";
                              return (
                                <div key={inst.id} className={"iio-sb2-inst"+(isInstOpen?" open":"")}>
                                  <button type="button" className="iio-sb2-instrow" title={inst.name}
                                    onClick={()=>setTreeExpInsts(p=>({...p,[inst.id]:!isInstOpen}))}>
                                    <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                                    <span className="led ok"/>
                                    <span className="lbl">{inst.name}</span>
                                  </button>
                                  <div className="iio-sb2-instbody"><div>
                                    <button type="button" className={"iio-sb2-leaf"+(isMonActive?" is-active":"")}
                                      onClick={()=>{navigate("dashboard",sp,inst,{tab:"monitor"});setMobileNavOpen(false);}}>
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h4l2.5-6 4 12 2.5-6h5"/></svg>
                                      <span className="lbl">{t("eintrag.messageMonitor")}</span>
                                    </button>
                                    <button type="button" className={"iio-sb2-leaf"+(isContentActive?" is-active":"")}
                                      onClick={()=>{navigate("dashboard",sp,inst,{tab:"content"});setMobileNavOpen(false);}}>
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9" opacity=".7"/></svg>
                                      <span className="lbl">{t("eintrag.schnittstellenContent")}</span>
                                    </button>
                                  </div></div>
                                </div>
                              );
                            })}
                          </div></div>
                        </div>
                      );
                    })}
                  </div>
                </div></div>
              </div>

              {[
                // dmk: AI Chat und KI iFlow Builder noch in DEV-Entwicklung - in der dmk-Sidebar ausgeblendet (Routen bleiben).
                // Monitor und iFlow Builder sind am 13.08.2026 ganz ausgebaut worden und stehen deshalb nicht mehr in dieser Liste.
                ...(window.location.hostname!=="dmk.tpase.de"||localStorage.getItem("featureWip")==="true" ? [
                {key:"chat",label:t("eintrag.aiChat"),active:page==="chat",onClick:()=>navigate("chat",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M20 11.5a7.5 7.5 0 0 1-11 6.6L4 20l1.9-5A7.5 7.5 0 1 1 20 11.5z"/><path d="M9 10.5h6M9 13.5h3.5" opacity=".55"/></svg>},
                {key:"ai-builder",label:t("eintrag.kiIflowBuilder"),active:page==="ai-builder",onClick:()=>navigate("ai-builder",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3.2"/><path d="M12 4v2M12 18v2M4 12h2M18 12h2M6.3 6.3l1.4 1.4M16.3 16.3l1.4 1.4M6.3 17.7l1.4-1.4M16.3 7.7l1.4-1.4" opacity=".55"/></svg>},
                ] : []),
                {key:"pipeline",label:t("eintrag.pipeline"),active:page==="pipeline",onClick:()=>navigate("pipeline",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="12" r="2.2"/><circle cx="19" cy="6" r="2.2"/><circle cx="19" cy="18" r="2.2"/><path d="M7.2 11.2 16.8 6.8M7.2 12.8l9.6 4.4" opacity=".55"/></svg>},
                ...(window.location.hostname!=="app.tpase.de"||localStorage.getItem("featureTransportPage")==="true" ? [{key:"transport-v2",label:t("eintrag.transport"),active:page==="transport-v2",onClick:()=>navigate("transport-v2",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9h16M4 15h16"/><path d="m16 6 4 3-4 3M8 12l-4 3 4 3"/></svg>}] : []),
              ].map(n=>(
                <button key={n.key} type="button" className={"iio-sb2-item"+(n.active?" is-active":"")} title={n.label} onClick={()=>{n.onClick();setMobileNavOpen(false);}}>
                  <span className="ic">{n.icon}</span><span className="lbl">{n.label}</span>
                </button>
              ))}
            </div>

            <div className="iio-sb2-sec">
              <div className="iio-sb2-label">{t("gruppe.governance")}</div>
              {[
                {key:"alerting",label:t("eintrag.alerting"),active:page==="alerting",onClick:()=>navigate("alerting",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M18 9a6 6 0 0 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 15 18 9z"/><path d="M10.3 20a2 2 0 0 0 3.4 0"/></svg>},
                {key:"pd",label:t("eintrag.partnerDirectory"),active:page==="pd",onClick:()=>navigate("pd",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3V4z"/><path d="M19 16H8a3 3 0 0 0-3 3" opacity=".55"/><path d="M9 8.5h6" opacity=".55"/></svg>},
                {key:"audit",label:t("eintrag.auditLog"),active:page==="audit",onClick:()=>navigate("audit",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4c-2.6-1.6-5.6-2-8-1.4V19c2.4-.6 5.4-.2 8 1.4 2.6-1.6 5.6-2 8-1.4V2.6c-2.4-.6-5.4-.2-8 1.4z"/><path d="M12 4v16.4" opacity=".4"/><path d="M6.5 8h3M6.5 11.5h3" opacity=".55"/></svg>},
                {key:"doku",label:t("eintrag.dokumentation"),active:page==="doku",onClick:()=>navigate("doku",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h9l4 4v14H6z"/><path d="M15 3v4h4" opacity=".55"/><path d="M9.5 12h6M9.5 15.5h4" opacity=".55"/></svg>},
              ].map(n=>(
                <button key={n.key} type="button" className={"iio-sb2-item"+(n.active?" is-active":"")} title={n.label} onClick={()=>{n.onClick();setMobileNavOpen(false);}}>
                  <span className="ic">{n.icon}</span><span className="lbl">{n.label}</span>
                </button>
              ))}
            </div>

            <div className="iio-sb2-sec">
              <div className="iio-sb2-label">{t("gruppe.administration")}</div>
              {[
                ...(user?.role==="admin"?[
                {key:"benutzer-anlegen",label:t("eintrag.benutzerAnlegen"),active:page==="benutzer-anlegen",onClick:()=>navigate("benutzer-anlegen",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2"/><path d="M3.5 20a5.5 5.5 0 0 1 11 0"/><path d="M17 11h4M19 9v4"/></svg>},
                {key:"benutzer-verwalten",label:t("eintrag.benutzerVerwalten"),active:page==="benutzer-verwalten",onClick:()=>navigate("benutzer-verwalten",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="7" r="3.4"/><path d="M2.5 21a6.5 6.5 0 0 1 13 0"/><circle cx="18.5" cy="9" r="2.4"/><path d="M15.5 21a4.2 4.2 0 0 1 6.5-3.5"/></svg>},
                {key:"ai-insights",label:t("eintrag.kiInsights"),active:page==="ai-insights",onClick:()=>navigate("ai-insights",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="5" cy="6" r="1.8"/><circle cx="18" cy="9" r="1.8"/><circle cx="9" cy="17" r="1.8"/><circle cx="17" cy="18" r="1.8"/><path d="M6.6 6.7 16.4 8.6M6.7 15.7 8 8M10.4 16.4l5.2 1.3M17.4 10.6l-.6 5.6" opacity=".55"/></svg>},
                ]:[]),
                ...((user?.role==="admin"||hatCicdZugang(user,spaces))?[
                {key:"cicd-setup",label:t("eintrag.cicdSetup"),active:page==="cicd-setup",onClick:()=>navigate("cicd-setup",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4M12 18v4M4.9 4.9l2.9 2.9M16.2 16.2l2.9 2.9M2 12h4M18 12h4M4.9 19.1l2.9-2.9M16.2 7.8l2.9-2.9"/><circle cx="12" cy="12" r="3"/></svg>},
                {key:"cicd-pipeline",label:t("eintrag.cicdMonitor"),active:page==="cicd-pipeline",onClick:()=>navigate("cicd-pipeline",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>},
                // Transportfreigabe: eine einzige Sidebar-Zeile, Route "approvals".
                // Sichtbar fuer globale Admins UND Space-Admins (hatCicdZugang).
                {key:"approvals",label:t("eintrag.transportFreigabe"),active:page==="approvals",onClick:()=>navigate("approvals",null,null),icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="m9 14 2 2 4-4"/></svg>},
                ]:[]),
              ].map(n=>(
                <button key={n.key} type="button" className={"iio-sb2-item"+(n.active?" is-active":"")} title={n.label} onClick={()=>{n.onClick();setMobileNavOpen(false);}}>
                  <span className="ic">{n.icon}</span><span className="lbl">{n.label}</span>
                </button>
              ))}
            </div>

          </nav>
            <div className={"iio-sb2-fade top"+(sbFades.top?" show":"")} aria-hidden="true"/>
            <div className={"iio-sb2-fade bot"+(sbFades.bot?" show":"")} aria-hidden="true"/>
          </div>

          {/* Zone 3: User fest verankert, Menü klappt nach oben */}
          <div className={"iio-sb2-foot"+(sbUserMenu?" open":"")}>
            <button type="button" className="iio-sb2-user" title={user.displayName||user.username}
              onClick={(e)=>{e.stopPropagation();setSbUserMenu(o=>!o);}}>
              <span className="ava">{user.displayName?.[0]?.toUpperCase()||"?"}</span>
              <span className="uinfo">
                <span className="uname">{user.displayName||user.username}</span>
                <span className="urole">{user.role==="admin"?t("benutzer.rolleAdmin"):t("benutzer.rolleBenutzer")}</span>
              </span>
              <svg className="uchev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </button>
            {sbUserMenu&&(
              <div className="iio-sb2-umenu" onClick={(e)=>e.stopPropagation()}>
                <button type="button" onClick={()=>{setSbUserMenu(false);setShowSettings(true);}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  {t("benutzer.einstellungen")}
                </button>
                <button type="button" onClick={()=>{toggleTheme();}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>
                  {t("benutzer.themaWechseln")}
                </button>
                <div className="sep"/>
                <button type="button" className="danger" onClick={()=>{setSbUserMenu(false);logout();}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5M21 12H9"/></svg>
                  {t("benutzer.abmelden")}
                </button>
              </div>
            )}
          </div>
        </aside>

        {/* === TOPBAR (durchgehend über die volle Breite) === */}
        <header className="iio-topbar">
          {/* Marke + Seitenleisten-Schalter. Beide Schalter meinen dasselbe Ziel
              (Navigation zeigen oder wegnehmen) und teilen sich denselben Platz -
              sichtbar ist immer nur einer, je nach Breite. */}
          <div className="iio-topbar-lead">
            <button type="button" className="iio-topbar-mobile-toggle" onClick={()=>setMobileNavOpen(o=>!o)} aria-label={t("seitenleiste.menue")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
            <button type="button" className="iio-topbar-rail-toggle" onClick={toggleSbRail} aria-pressed={sbRail} title={sbRail?t("seitenleiste.ausklappen"):t("seitenleiste.einklappen")} aria-label={sbRail?t("seitenleiste.ausklappen"):t("seitenleiste.einklappen")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M9 4v16"/></svg>
            </button>
            <button type="button" className="iio-topbar-brand" onClick={()=>{setTargetTab(null);setTargetFlowName(null);setDashboardSubTab("monitor");navigate("spaces",null,null);loadSpaces();setMobileNavOpen(false);}} title={branding.title||"Integration in One"}>
              <span className="iio-topbar-logo">
                {branding.logoUrl
                  ? <img src={branding.logoUrl} alt=""/>
                  : <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 26a6 6 0 0 1-.9-11.93A8.5 8.5 0 0 1 26.6 12.3 6.5 6.5 0 0 1 30 24.8"/>
                      <path d="M13 30h14"/><path d="M13 30l3-3M13 30l3 3"/><path d="M27 30l-3-3M27 30l-3 3" opacity=".45"/>
                      <circle cx="20" cy="19" r="1.4" fill="currentColor" stroke="none"/><path d="M20 20.5V26" opacity=".6"/>
                    </svg>}
              </span>
              <span className="iio-topbar-wm">
                <span className="w1">{branding.title||"Integration in One"}</span>
                {/* Der Untertitel kommt aus dem Branding. Hier stand fest
                    "Mission Control", der eingestellte Wert wurde also nie
                    angezeigt. Ist er leer, entfaellt die Zeile ganz, statt
                    3px Aussenabstand und eine leere Zeilenhoehe zu erzeugen. */}
                {branding.subtitle&&<span className="w2">{branding.subtitle}</span>}
              </span>
            </button>
          </div>

          {/* Steht ganz rechts neben Thema und Abmelden - geschoben vom
              auto-Margin in .iio-topbar-search, nicht von der Reihenfolge hier.
              Die bleibt so, damit die Tabulatorfolge der Leserichtung folgt. */}
          {/* Das Feld hat keine sichtbare Beschriftung, seinen Namen trug bisher
              allein der Platzhalter - und der verschwindet, sobald jemand tippt.
              aria-label traegt ihn dauerhaft, role="search" macht den Bereich
              als Sprungziel auffindbar. */}
          <div className="iio-topbar-search" role="search" style={{position:"relative"}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={searchQuery} onChange={e=>handleSearch(e.target.value)} placeholder={t("suche.platzhalter")} aria-label={t("suche.platzhalter")}/>
            {searchResults&&searchQuery&&(
              <div className="iio-search-results">
                {searchResults.instances?.length>0&&<>
                  <div className="iio-search-section">{t("suche.instanzen")}</div>
                  {searchResults.instances.map(i=>(
                    <button key={i.id} className="iio-search-item" onClick={()=>{setSearchQuery("");setSearchResults(null);const sp=spaces.find(s=>s.id===i.spaceId);navigate("dashboard",sp,i);}}>
                      <span>{i.name}</span><span className="iio-search-meta">{i.spaceName}</span>
                    </button>
                  ))}
                </>}
                {searchResults.iflows?.length>0&&<>
                  <div className="iio-search-section">{t("suche.iflows")}</div>
                  {searchResults.iflows.slice(0,10).map((f,idx)=>(
                    <button key={idx} className="iio-search-item" onClick={()=>{setSearchQuery("");setSearchResults(null);const sp=spaces.find(s=>s.id===f.spaceId);const inst={id:f.instanceId,name:f.instanceName,spaceId:f.spaceId};navigate("dashboard",sp,inst);}}>
                      <span>{f.Name}</span><span className="iio-search-meta">{f.instanceName}</span>
                    </button>
                  ))}
                </>}
                {!searchResults.instances?.length&&!searchResults.iflows?.length&&(
                  <div className="iio-search-empty">{t("suche.keineErgebnisse")}</div>
                )}
              </div>
            )}
          </div>

          {/* Die Verbindungsanzeige stand fest auf "Verbunden" und war an keinen
              Zustand gebunden - sie sagte nichts aus und ist entfallen. */}
          <div className="iio-topbar-right">
            <button type="button" className="iio-icon-btn" onClick={toggleTheme} title={t("benutzer.themaWechseln")}>
              {theme==="dark"
                ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
                : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>}
            </button>
            <button type="button" className="iio-icon-btn" onClick={logout} title={t("benutzer.abmelden")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>
        </header>

        {/* === MAIN === */}
        <main className={"iio-main"+(page==="pipeline"||page==="transport"||page==="transport-v2"||page==="ai-builder"||page==="ai-insights"||page==="doku"?" iio-main--full":"")}>
          {showSettings&&<SettingsModal onClose={()=>setShowSettings(false)} user={user} onUpdate={u=>setUser(prev=>({...prev,...u,hasLlmConfig:true}))} onBrandingUpdate={setBranding}/>}
          {page==="spaces"&&<SpacesHome spaces={spaces} cicdStatus={cicdStatus} onRefresh={loadSpaces} onOpenSpace={(s,inst)=>{if(inst){navigate("dashboard",s,inst);}else{navigate("space",s,null);}}}/>}
          {page==="space"&&activeSpace&&<SpaceDetail space={activeSpace} cicdStatus={cicdStatus} onBack={()=>{navigate("spaces",null,null);loadSpaces();}} onSpaceUpdated={updated=>{setActiveSpace(s=>({...s,...updated}));loadSpaces();}} onOpenInstance={(i,flowName)=>{setTargetFlowName(flowName||null);navigate("dashboard",undefined,i);}}/>}
          {page==="space"&&!activeSpace&&<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"50vh",color:"var(--iio-text-3)"}}>{t("gemeinsam:zustand.laedt")}</div>}
          {page==="dashboard"&&activeInst&&<Dashboard instance={activeInst} onBack={()=>{setTargetFlowName(null);navigate("space");}} targetFlowName={targetFlowName} onClearTarget={()=>setTargetFlowName(null)} targetTab={targetTab} onClearTargetTab={()=>setTargetTab(null)}/>}
          {page==="dashboard"&&!activeInst&&<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"50vh",color:"var(--iio-text-3)"}}>{t("gemeinsam:zustand.laedt")}</div>}
          {page==="chat"&&<AiChat spaces={spaces} prefillMessage={chatPrefill} onPrefillConsumed={()=>setChatPrefill(null)}/>}
          {page==="ai-builder"&&<AiIflowBuilder/>}
          {page==="alerting"&&<AlertingPage spaces={spaces} onNavigate={navigate}/>}
          {page==="audit"&&<AuditLog/>}
          {page==="approvals"&&<TransportApprovals user={user} spaces={spaces}/>}
          {page==="pastebin"&&<PastebinPage/>}
          {page==="pd"&&<PartnerDirectory spaces={spaces} preselectedInstId={pdPreselectedInst} preselectedPid={pdPreselectedPid}/>}
          {page==="pixel-art"&&<PixelArt/>}
          {page==="doku"&&<DokuPage spaces={spaces} branding={branding}/>}
          {page==="transport-v2"&&<TransportV2Page spaces={spaces} user={user}/>}
          {page==="pipeline"&&<PipelineTopology spaces={spaces} user={user} onNavigate={navigate}/>}
          {page==="benutzer-anlegen"&&<BenutzerAnlegen user={user} spaces={spaces} onDone={()=>navigate("spaces",null,null)}/>}
          {page==="benutzer-verwalten"&&<BenutzerVerwalten user={user} spaces={spaces}/>}
          {page==="cicd-setup"&&<CicdSetup user={user} spaces={spaces}/>}
          {/* onCicdRefresh: nach dem Loesen einer Bindung im Monitor stimmt die Markierung an den
              Spaces nicht mehr. Sie wird sonst nur einmal beim Start geladen und bliebe stehen. */}
          {page==="cicd-pipeline"&&<CicdPipeline user={user} spaces={spaces} onCicdRefresh={loadCicdStatus}/>}
          {page==="ai-insights"&&<AiInsights/>}
          {page==="invite"&&inviteToken&&<InviteAccept token={inviteToken} onDone={()=>{navigate("spaces",null,null);loadSpaces();}}/>}
        </main>

        {/* === FOOTER === */}
        <footer className="iio-footer">
          <span>abat AG · 2026</span>
          <span className="iio-sep">·</span>
          <span>{branding.title||"Integration in One"} · v2.0</span>
          <div className="iio-footer-right">
            <span>{t("fuss.hilfe")}</span>
          </div>
        </footer>

      </div>
    </div>
      {user && !user.mustChangePassword && <>
        <ChatBubble open={chatOpen} onClick={()=>setChatOpen(o=>!o)} hasAiInstances={hasAiInstances} />
        <ChatPanel open={chatOpen} onClose={()=>setChatOpen(false)} spaces={spaces} activeInst={activeInst} />
      </>}
    </ThemeCtx.Provider>
    </ThemeProvider>
    </ErrorBoundary>
    </QueryClientProvider>
  );
}
